import React, { useState, useEffect } from 'react';
import axios from 'axios';

function FileManager({ onClose }) {
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    uploadType: 'all',
    uploaderRole: 'all'
  });

  useEffect(() => {
    fetchFiles();
    fetchStats();
  }, [currentPage, filters]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/file-uploads', {
        params: {
          page: currentPage,
          limit: 20,
          ...filters
        }
      });
      setFiles(response.data.files);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching files:', error);
      alert('Error loading files');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/file-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const deleteFile = async (fileId, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`/api/file-uploads/${fileId}`);
      alert('File deleted successfully');
      fetchFiles();
      fetchStats();
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error deleting file: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return '🖼️';
    if (mimeType?.startsWith('video/')) return '🎥';
    if (mimeType?.startsWith('audio/')) return '🎵';
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('document') || mimeType?.includes('word')) return '📝';
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return '📊';
    return '📁';
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.8)',
      zIndex: 10000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div style={{
        background: 'white',
        width: '95%',
        height: '95%',
        borderRadius: '15px',
        padding: '20px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '15px',
          borderBottom: '2px solid #e0e0e0'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>📁 File Management System</h2>
          <button
            onClick={onClose}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            ✕ Close
          </button>
        </div>

        {/* Statistics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '20px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            border: '2px solid #2196f3'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1565c0' }}>
              {stats.totalFiles || 0}
            </div>
            <div style={{ color: '#1565c0', fontSize: '14px' }}>Total Files</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            border: '2px solid #9c27b0'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6a1b9a' }}>
              {formatFileSize(stats.totalSize || 0)}
            </div>
            <div style={{ color: '#6a1b9a', fontSize: '14px' }}>Total Size</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            border: '2px solid #4caf50'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
              1MB
            </div>
            <div style={{ color: '#2e7d32', fontSize: '14px' }}>Size Limit</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '15px',
          marginBottom: '20px',
          padding: '15px',
          background: '#f8f9fa',
          borderRadius: '10px',
          alignItems: 'center'
        }}>
          <div>
            <label style={{ fontWeight: 'bold', marginRight: '8px' }}>File Type:</label>
            <select
              value={filters.uploadType}
              onChange={(e) => setFilters({ ...filters, uploadType: e.target.value })}
              style={{
                padding: '8px',
                border: '2px solid #007bff',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Types</option>
              <option value="profile">Profile Pictures</option>
              <option value="content">Content Files</option>
              <option value="document">Documents</option>
            </select>
          </div>
          <div>
            <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Uploader Role:</label>
            <select
              value={filters.uploaderRole}
              onChange={(e) => setFilters({ ...filters, uploaderRole: e.target.value })}
              style={{
                padding: '8px',
                border: '2px solid #007bff',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Roles</option>
              <option value="student">Students</option>
              <option value="teacher">Teachers</option>
              <option value="admin1">Admin Level 1</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: '14px', color: '#666' }}>
            Showing {files.length} files (Page {currentPage} of {totalPages})
          </div>
        </div>

        {/* File List */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          background: '#fafafa'
        }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>⏳</div>
              <div>Loading files...</div>
            </div>
          ) : files.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>📂</div>
              <div>No files found</div>
            </div>
          ) : (
            <div style={{ padding: '10px' }}>
              {files.map(file => (
                <div key={file._id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '15px',
                  background: 'white',
                  borderRadius: '8px',
                  marginBottom: '10px',
                  border: '1px solid #e0e0e0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                    <div style={{ fontSize: '24px' }}>
                      {getFileTypeIcon(file.mimeType)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
                        {file.originalName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                        Uploaded by: <strong>{file.uploadedBy}</strong> ({file.uploaderRole})
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Size: {formatFileSize(file.fileSize)} | 
                        Type: {file.uploadType} | 
                        Date: {new Date(file.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <a
                      href={file.uploadPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: '#28a745',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      👁️ View
                    </a>
                    <button
                      onClick={() => deleteFile(file._id, file.originalName)}
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            marginTop: '15px',
            padding: '15px',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              style={{
                background: currentPage === 1 ? '#e0e0e0' : '#007bff',
                color: currentPage === 1 ? '#666' : 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '6px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              ← Previous
            </button>
            <span style={{ fontWeight: 'bold' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              style={{
                background: currentPage === totalPages ? '#e0e0e0' : '#007bff',
                color: currentPage === totalPages ? '#666' : 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '6px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default FileManager;